import React from 'react';
import ReactDOM from 'react-dom/client';
import { installGlobalErrorReporter } from '@pc/core';
import { initAnalytics } from '@pc/analytics-client';
import { GlobalErrorBoundary, installChunkReloadHandler } from '@pc/ui';
import { getSession } from '@pc/db';
import { initI18n } from '@pc/i18n';
import { App } from './App';
import { env } from './env';
import { getActiveContextForReporter } from './shell/getActiveContextForReporter';
import './index.css';

// Backstop for the stale-chunk failure mode every Vite SPA hits on
// deploy: a user with the old shell loaded clicks a nav link,
// React.lazy fires `import('/assets/SettingsBillingPage-<oldhash>.js')`,
// Netlify 404s the purged chunk, and react-router renders "Unexpected
// Application Error! Failed to fetch dynamically imported module".
// Code-split routes in App.tsx already use `lazyWithReload`; this
// listener catches every OTHER dynamic import (i18n bundles, third-party
// libs, ad-hoc `await import()` inside event handlers). MUST run before
// any React mount so the listener is attached when the first chunk
// lands.
installChunkReloadHandler();

// react-i18next bootstrap. Required because some lazy routes (the Inbox
// in particular, via @pc/shop-ui's MessageBubble -> useTranslatedContent)
// call useTranslation(). Without an initialized i18next instance the
// hook warns at runtime + auto-translation falls back to the source
// language. The operator app doesn't expose a UI language picker yet,
// so the locale follows browser Accept-Language / localStorage; that's
// enough to drive the message-translation UX in the inbox.
initI18n({ app: 'web' });

// Install the global error reporter BEFORE anything else (in particular,
// before initSupabase) so a failure during DB init still lands in the
// errors pipeline. The reporter wires up window.onerror,
// window.onunhandledrejection, a console.error breadcrumb wrapper,
// fetch instrumentation, and click/navigation breadcrumbs.
installGlobalErrorReporter({
  app: 'web',
  supabaseUrl: env.supabaseUrl,
  anonKey: env.supabaseAnonKey,
  release: (import.meta.env.VITE_BUILD_SHA as string | undefined) ?? null,
  environment: (import.meta.env.MODE as string | undefined) ?? 'production',
  getActiveContext: getActiveContextForReporter,
});

// Self-hosted pageview analytics. The AppShell mounts useRouteTracking()
// so we only configure the singleton here.
initAnalytics({
  app: 'web',
  supabaseUrl: env.supabaseUrl,
  anonKey: env.supabaseAnonKey,
  getSession: async () => {
    const s = await getSession();
    return s ? { access_token: s.access_token } : null;
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found.');

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <GlobalErrorBoundary>
      <App />
    </GlobalErrorBoundary>
  </React.StrictMode>,
);
